<template>
  <div class="home">
    <div class="container is-fluid">
      <page-header :entity="$entitiesName.Evaluation">
        <h1 class="title">
          {{ $t("evaluation.evaluation") }}
        </h1>
        <template slot="actions">
          <form
            class="inline-form"
            method="POST"
            target="_blank"
            :action="`${API}/evaluation/export?${params}`"
          >
            <button type="submit" class="button is-primary">
              {{ $t("common.exporter") }}
            </button>
            <input type="hidden" name="token" :value="authToken" />
          </form>
        </template>
      </page-header>
      <div class="bg-white">
        <div class="filters-container">
          <div class="filters">
            <div class="filter-item">
              <many2one-field
                :label="$t('evaluation.companies')"
                :fetch="
                  $api.fetchCompaniesForEvaluation.bind(this, agenciesIds)
                "
                objectName="companies"
                v-model="companies"
                reference="name"
                :columns="companiesColumns"
                :hasFilter="true"
                :inline="false"
                :edit="true"
              ></many2one-field>
            </div>
            <div class="filter-item">
              <many2one-field
                :label="$t('evaluation.agencies')"
                :fetch="
                  $api.fetchAgenciesForEvaluation.bind(this, companiesIds)
                "
                v-model="agencies"
                reference="edifice"
                :columns="agencyColumns"
                :hasFilter="true"
                :inline="false"
                :edit="true"
              ></many2one-field>
            </div>
            <div class="filter-item">
              <many2one-field
                :label="$t('evaluation.contracts')"
                :fetch="$api.fetchContracts"
                v-model="contracts"
                reference="contractNumber"
                :columns="contractsColumns"
                :hasFilter="true"
                :inline="false"
                :edit="true"
              ></many2one-field>
            </div>
            <div class="filter-item">
              <many2one-field
                :label="$t('evaluation.projects')"
                :fetch="$api.fetchProjects"
                v-model="projects"
                reference="name"
                :columns="projectsColumns"
                :hasFilter="true"
                :inline="false"
                :edit="true"
              ></many2one-field>
            </div>
            <div class="filter-item">
              <many2one-field
                :label="$t('evaluation.subcontractorsMarkets')"
                :fetch="$api.fetchMarkets"
                v-model="markets"
                reference="edifice"
                :columns="marketsColumns"
                :hasFilter="true"
                :inline="false"
                :edit="true"
              ></many2one-field>
            </div>
            <div class="filter-item average">
              <div class="input-label-eval">
                {{ $t("evaluation.averageRatings") }}
              </div>
              <div class="average-input-container">
                <label class="average-input-label">
                  {{ $t("common.between") }}
                </label>

                <text-field
                  type="number"
                  v-model.number="min"
                  :inline="false"
                  :edit="true"
                />
                <label class="average-input-label">
                  {{ $t("common.and") }}
                </label>

                <text-field
                  type="number"
                  v-model.number="max"
                  :inline="false"
                  :edit="true"
                />
              </div>
            </div>
            <div class="filter-item date">
              <div class="input-label-eval">
                {{ $t("evaluation.ratingDate") }}
              </div>
              <div class="date-field-container">
                <date-field
                  class="date-field-eval"
                  v-model="start_date"
                  :inline="false"
                  :edit="true"
                ></date-field>
                <date-field
                  class="date-field-eval"
                  v-model="end_date"
                  :inline="false"
                  :edit="true"
                ></date-field>
              </div>
            </div>
          </div>
          <div class="text-center m-button">
            <button
              :class="{ 'is-loading': loading }"
              class="button is-warning"
              @click="HandleFiltering()"
            >
              {{ $t("evaluation.search") }}
            </button>
          </div>
        </div>
        <div>
          <div class="columns" style="margin: 0px">
            <div class="column is-half" style="min-width: 600px">
              <div class="border" style="padding: 10px">
                <h2 class="is-size-6">{{ $t("evaluation.companyList") }}</h2>
                <div class="data-table-container">
                  <evaluation-table
                    ref="evaluationTable"
                    :fetch="$api.fetchCompaniesEvaluations"
                    :paginateOptions="[10, 20, 50]"
                    :paramsFilters="params"
                  />
                </div>
              </div>
            </div>

            <div
              class="columns"
              style="width: 100%; margin: 0px; min-width: 600px"
            >
              <div class="column">
                <div class="border">
                  <h2 class="is-size-6">{{ $t("evaluation.ratingNumber") }}</h2>
                  <div class="eval-number-box">{{ totalEvaluations }}</div>
                </div>
                <div class="border">
                  <h2 class="is-size-6">
                    {{ $t("evaluation.averageRatings") }}
                  </h2>
                  <div style="height: 350px; align-content: center">
                    <div class="chart-container">
                      <div class="overlay-circle"></div>
                      <gauge-chart
                        :value="averageScore"
                        :minValue="-1.6"
                        :maxValue="5"
                        class="gauge-chart"
                      />
                      <div class="value-display">{{ averageScore }}</div>
                      <div class="min-value">0.0</div>
                      <div class="max-value">5.0</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="border">
                  <h2 class="is-size-6">{{ $t("evaluation.agencyNumber") }}</h2>
                  <div class="eval-number-box">{{ totalAgencies }}</div>
                </div>
                <div class="border">
                  <h2 class="is-size-6">{{ $t("evaluation.gaugeRadar") }}</h2>
                  <div class="chart-radar-container">
                    <radar-chart
                      :chartData="chartData"
                      :options="chartOptions"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "evaluation",

  data() {
    return {
      API: axios.defaults.baseURL,
      authToken: this.$store.getters["auth/getToken"],
      companies: [],
      agencies: [],
      min: 0,
      max: 10,
      start_date: "2000-01-01",
      end_date: new Date(new Date().setDate(new Date().getDate() + 1))
        .toISOString()
        .slice(0, 10),
      markets: [],
      contracts: [],
      projects: [],
      summaryData: {},
      totalEvaluations: 0,
      totalAgencies: 0,
      totalProjects: 0,
      averageScore: 0,
      companiesLength: 0,
      radarNotations: [0, 0, 0, 0, 0],
      loading: false,

      companiesColumns: {
        name: this.$t("common.name"),
        edifice: this.$t("common.edifice"),
        siren: this.$t("common.siren"),
        manager: this.$t("common.manager"),
        mail: this.$t("common.mail"),
      },
      agencyColumns: {
        edifice: this.$t("common.agency"),
        siret: this.$t("common.siret"),
        manager: this.$t("common.manager"),
        "company.name": this.$t("common.company"),
      },
      contractsColumns: {
        contractNumber: this.$t("contract.contractNumber"),
        "agency.edifice": this.$t("common.agency"),
        "parent.contractNumber": this.$t("contract.parentContract"),
        signatureDate: this.$t("contract.signatureDate"),
      },
      projectsColumns: {
        name: this.$t("common.name"),
        code: this.$t("common.code"),
        "type.name": this.$t("common.type"),
        manager: this.$t("common.manager"),
      },
      marketsColumns: {
        edifice: this.$t("common.edifice"),
        "contract.contractNumber": this.$t("markets.columns.contractNumber"),
        "agency.edifice": this.$t("common.agencies"),
        "project.name": this.$t("common.project"),
      },
      chartData: {
        labels: [
          "Relationnel",
          "RSE",
          "Qualité",
          "Réactivité",
          "Technique",
          "Sécurité",
        ],
        datasets: [
          {
            label: "Valeurs",
            backgroundColor: "rgba(159, 209, 255, 0.5)",
            borderColor: "rgba(159, 209, 255, 0.5)",
            pointBackgroundColor: "#118DF8",
            pointHoverBackgroundColor: "#fff",
            pointBorderWidth: 5,
            pointBorderColor: "#108CFC",
            data: this.radarNotations,
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          r: {
            max: 5,
            min: 0,
            ticks: {
              display: false,
            },
          },
        },
        plugins: {
          legend: {
            position: "bottom",
            align: "end",
            labels: {
              boxWidth: 12,
              padding: 10,
            },
          },
        },
      },
    };
  },

  computed: {
    companiesIds() {
      if (this.companies.length === 0) return [];

      return this.companies.reduce((acc, val) => {
        return [...acc, val.id];
      }, []);
    },
    agenciesIds() {
      if (this.agencies.length === 0) return [];

      return this.agencies.reduce((acc, val) => {
        return [...acc, val.id];
      }, []);
    },
    projectsIds() {
      if (this.projects.length === 0) return [];

      return this.projects.reduce((acc, val) => {
        return [...acc, val.id];
      }, []);
    },
    contractsIds() {
      if (this.contracts.length === 0) return [];

      return this.contracts.reduce((acc, val) => {
        return [...acc, val.id];
      }, []);
    },
    marketsIds() {
      if (this.markets.length === 0) return [];

      return this.markets.reduce((acc, val) => {
        return [...acc, val.id];
      }, []);
    },

    params() {
      return (
        "start_date=" +
        this.start_date +
        "&end_date=" +
        this.end_date +
        "&companies=" +
        this.companiesIds +
        "&agencies=" +
        this.agenciesIds +
        "&min=" +
        this.min +
        "&max=" +
        this.max +
        "&projects=" +
        this.projectsIds +
        "&contracts=" +
        this.contractsIds +
        "&markets=" +
        this.marketsIds
      );
    },
  },

  mounted() {
    this.fetchEvaluationSummary();
  },

  methods: {
    HandleFiltering() {
      this.loading = true;
      this.$refs.evaluationTable.refreshWithFilters(this.params);
      this.fetchEvaluationSummary();
    },

    fetchEvaluationSummary() {
      this.$api
        .fetchEvaluationSummary(this.params)
        .then((response) => {
          this.summaryData = response;
          this.averageScore = parseFloat(this.summaryData.score.toFixed(1));
          this.totalProjects = this.summaryData.project.count;
          this.totalEvaluations = this.summaryData.count;
          this.totalAgencies = this.summaryData.agencies;

          // Create arrays for labels and data with only non-zero categories
          const categories = [
            {
              label: "Relationnel",
              value: this.summaryData.procurement.relational.score,
              count: this.summaryData.procurement.relational.count,
            },
            {
              label: "RSE",
              value: this.summaryData.procurement.rse.score,
              count: this.summaryData.procurement.rse.count,
            },
            {
              label: "Qualité",
              value: this.summaryData.project.quality.score,
              count: this.summaryData.project.quality.count,
            },
            {
              label: "Réactivité",
              value: this.summaryData.project.reactivity.score,
              count: this.summaryData.project.reactivity.count,
            },
            {
              label: "Technique",
              value: this.summaryData.project.technic.score,
              count: this.summaryData.project.technic.count,
            },
            {
              label: "Sécurité",
              value: this.summaryData.safety.score,
              count: this.summaryData.safety.count,
            },
          ].filter((category) => category.count > 0);

          // Update chart data with filtered categories
          this.chartData.labels = categories.map((c) => c.label);
          this.chartData.datasets[0].data = categories.map((c) =>
            c.value.toFixed(1)
          );
        })
        .catch((error) => {
          console.error("Error fetching agency evaluations:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.border {
  color: black;
  border: 1px solid #dbdbdb;
  border-radius: 3px;
  margin-bottom: 15px;
  h2 {
    padding: 5px;
  }
}
.eval-col-bg {
  background-color: white;
  margin: 10px;
}

.bg-white {
  background-color: white;
}

.eval-number-box {
  height: 80px;
  background: white;
  align-content: space-evenly;
  text-align: center;
  font-size: xxx-large;
}

.filters-container {
  background: #019fc4;
  display: flex;
}

.filters {
  display: flex;
  margin: 30px 0px;
  flex-wrap: wrap;
  flex-grow: 1;
}

.filter-item {
  flex: 1;
  padding: 5px 10px;
  background-color: white;
  border-radius: 5px;
  margin: 10px;
  transition-duration: 1s;
  transition-property: max-height;
  max-height: 100px;
  overflow-y: auto;
  min-width: 150px;
  & > * {
    margin: 0 !important;
  }
  &.date {
    min-width: 300px;
    max-width: 300px;
  }
  &.average {
    min-width: 200px;
    max-width: 200px;
  }
}

.m-button {
  margin: auto 10px;
}

.overlay-circle {
  width: 230px;
  height: 230px;
  position: absolute;
  border-radius: 50%;
  top: 10px;
  left: 10px;
  border: solid 8px rgb(1, 159, 196);
  z-index: 1;
}

.chart-container {
  width: 250px;
  height: 250px;
  position: relative;
}

canvas {
  width: 100%;
  height: 100%;
  display: block;
  box-sizing: border-box;
}

.chart-radar-container {
  display: flex;
  justify-content: center;
  background-color: white;
  height: 350px;
}

.input-label-eval {
  font-weight: bold;
  vertical-align: top;
  padding-right: 4px;
}

.average-input-container {
  display: flex;
  width: 100%;
  align-items: center;
  div {
    margin-bottom: 0px;
  }
}
.date-field-container {
  display: flex;
  justify-content: space-between;
  div {
    margin-bottom: 0px;
  }
}
.date-field-eval {
  margin-right: 10px;

  input[type="date"]::-webkit-calendar-picker-indicator {
    position: absolute;
    right: 0%;
  }
}

.average-input-label {
  margin-right: 5px;
}

.text-center {
  text-align: center;
}

.value-display {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 24px;
  font-weight: bold;
}
.min-value {
  position: absolute;
  bottom: 67px;
  left: 80px;
  font-size: 12px;
}

.max-value {
  position: absolute;
  bottom: 67px;
  right: 80px;
  font-size: 12px;
}
</style>
